import { useState } from 'react';
import { Caption1, MessageBar, MessageBarBody, Radio, RadioGroup, Spinner, makeStyles } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import type { OfferGroup } from '@vigeo/shared';
import { OffersTable, offerGroupCopy, showingFirstCandidates } from '@/features/offers';
import { fetchCatalogOptions, fetchOffers } from '@/lib/api';
import { fetchAuthState } from '@/lib/auth';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  /** Page-name voice (24/700), as on People, Review and the Person Overview. */
  title: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightBold },
  filters: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  /**
   * A 44px TARGET ON A PHONE, which nothing was checking until #331 put this screen in the Browser
   * QA sweep. All seven filters measured 32×32 — Fluent's own indicator size.
   *
   * THE SAME TWO RULES `NewCandidateForm`'s role tiles carry, in the same shape, and the shape is
   * the point: the stretch is unconditional and only the floor is phone-only. A first attempt put
   * both inside the media query, on the GROUP rather than on each chip, and neither applied —
   * the chips still measured 32×32.
   *
   * Fluent sizes `.fui-Radio__input` to the indicator and centres the label beside it, so most of
   * the chip listens for nothing. Stretching the input makes the whole chip select the filter; it
   * is `opacity: 0`, so this changes what the chip responds to, not how it looks.
   */
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    '& .fui-Radio__input': { width: '100%', height: '100%' },
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
});

const ALL = 'all';

/**
 * Offers (#259, frame 06b `293:444`): every offer across candidates, grouped by where it stands.
 *
 * Nothing here is stored — the API derives each row on the read — and nothing on this page acts
 * on an offer. Drafting, reviewing and sending are the person's Hiring section until the offer
 * screens land (#260–#263), so every button is a link to the candidate.
 */
export function OffersPage() {
  const styles = useStyles();
  const [filter, setFilter] = useState<OfferGroup | typeof ALL>(ALL);
  const offers = useQuery({ queryKey: ['offers'], queryFn: fetchOffers });
  // Shared with the new-candidate form, so the role names cost no extra request.
  const catalog = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, staleTime: Infinity });
  // The same gate every other staff screen reads; a Viewer reads this list and acts on nothing.
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;

  const roleLabels = new Map((catalog.data?.jobProfiles ?? []).map((profile) => [profile.key, profile.label]));
  /**
   * EVERY CANDIDATE ON THE SCREEN, the readable rows and the degraded ones (#299).
   *
   * This drives "All (n)" and the empty state. Counting only `rows` would say "All (3)" above four
   * people, and — worse — would show "No candidates are in hiring right now" on a screen whose
   * every candidate happened to be degraded, which is the silent disappearance the degraded row
   * exists to prevent.
   */
  const unavailable = offers.data?.unavailable ?? [];
  const total = (offers.data?.rows.length ?? 0) + unavailable.length;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Offers</h1>
        <Caption1>Every offer across candidates, from the first draft to the signed copy.</Caption1>
      </header>

      {offers.isPending ? <Spinner size="small" label="Loading offers" /> : null}

      {offers.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>Offers could not be loaded. Try again shortly.</MessageBarBody>
        </MessageBar>
      ) : null}

      {offers.data === undefined ? null : (
        <>
          <RadioGroup
            className={styles.filters}
            layout="horizontal"
            aria-label="Show"
            value={filter}
            onChange={(_, data) => setFilter(data.value as OfferGroup | typeof ALL)}
          >
            <Radio className={styles.chip} value={ALL} label={`All (${total})`} />
            {offerGroupCopy.map((group) => (
              <Radio className={styles.chip} key={group.id} value={group.id} label={`${group.title} (${offers.data.counts[group.id]})`} />
            ))}
          </RadioGroup>

          {/* The bound is the API's and arrives on the response (#300); the screen never assumes it. */}
          {offers.data.truncated ? (
            <MessageBar intent="warning">
              <MessageBarBody>{showingFirstCandidates(offers.data.counted)}</MessageBarBody>
            </MessageBar>
          ) : null}

          {total === 0 ? (
            <MessageBar intent="info">
              <MessageBarBody>No candidates are in hiring right now. Add a candidate to start an offer.</MessageBarBody>
            </MessageBar>
          ) : (
            <OffersTable
              rows={offers.data.rows}
              counts={offers.data.counts}
              unavailable={unavailable}
              only={filter === ALL ? null : filter}
              roleLabels={roleLabels}
              canEdit={canEdit}
            />
          )}
        </>
      )}
    </div>
  );
}
